import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../config/api';

const AdminAuthContext = createContext(null);

/** Synchronous read so children’s first useEffect sees a real token (avoids Bearer undefined). */
function readValidSessionFromStorage() {
  try {
    const storedToken = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');
    if (!storedToken || !storedUser) return null;
    const payload = storedToken.split('.')[1];
    if (!payload) return null;
    const tokenData = JSON.parse(atob(payload));
    const now = Math.floor(Date.now() / 1000);
    if (!tokenData.exp || tokenData.exp <= now) return null;
    return {
      token: storedToken,
      user: JSON.parse(storedUser),
    };
  } catch {
    return null;
  }
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const initial = readValidSessionFromStorage();
  const [user, setUser] = useState(() => initial?.user ?? null);
  const [token, setToken] = useState(() => initial?.token ?? null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(initial));
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [loginEmail, setLoginEmail] = useState(null); // Store email from login step

  useEffect(() => {
    const s = readValidSessionFromStorage();
    if (!s) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminLoginTime');
      localStorage.removeItem('cognitoTokens');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // Login with email and password
  const login = useCallback(async (email, password) => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_AUTH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'login',
          email,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Check if MFA is required
      if (data.requiresMFA || data.requiresEmailOTP) {
        setMfaSession(data.session);
        setMfaRequired(true);
        setLoginEmail(email); // Store email for MFA verification
        setMfaChallenge({
          type: data.challengeName || 'EMAIL_OTP',
          destination: data.destination || email,
          message: data.message || 'Verification code sent to your email'
        });
        return {
          success: false,
          requiresMFA: true,
          challenge: data.challengeName || 'EMAIL_OTP',
          session: data.session,
          destination: data.destination
        };
      }

      // No MFA required - direct login success
      if (data.success && (data.token || data.cognitoTokens?.IdToken)) {
        // Use Cognito IdToken as primary for API Gateway JWT authorizers
        const finalToken = data.cognitoTokens?.IdToken || data.token;
        setToken(finalToken);
        setUser(data.user);
        setIsAuthenticated(true);
        localStorage.setItem('adminToken', finalToken);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        if (data.cognitoTokens) {
          localStorage.setItem('cognitoTokens', JSON.stringify(data.cognitoTokens));
        }
        localStorage.setItem('adminLoginTime', new Date().toISOString());

        return {
          success: true,
          user: data.user,
          token: data.token
        };
      }

      throw new Error('Unexpected login response');
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  }, []);

  // Verify MFA code
  const verifyMFA = useCallback(async (mfaCode) => {
    if (!mfaSession) {
      throw new Error('No MFA session found');
    }

    // Get email from login step (stored when MFA was required)
    const emailForMFA = loginEmail || user?.email || '';

    if (!emailForMFA) {
      throw new Error('Email not found. Please login again.');
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_AUTH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'mfa-verify',
          email: emailForMFA,
          mfaCode,
          session: mfaSession,
          challengeName: mfaChallenge?.type || 'EMAIL_OTP'
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'MFA verification failed');
      }

      // MFA verified successfully
      const finalToken = data.cognitoTokens?.IdToken || data.token;
      setToken(finalToken);
      setUser(data.user);
      setIsAuthenticated(true);
      setMfaRequired(false);
      setMfaSession(null);
      setMfaChallenge(null);
      setLoginEmail(null); // Clear stored email

      localStorage.setItem('adminToken', finalToken);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      if (data.cognitoTokens) {
        localStorage.setItem('cognitoTokens', JSON.stringify(data.cognitoTokens));
      }
      localStorage.setItem('adminLoginTime', new Date().toISOString());

      return {
        success: true,
        user: data.user,
        token: data.token
      };
    } catch (error) {
      console.error('MFA verification error:', error);
      return {
        success: false,
        error: error.message || 'MFA verification failed'
      };
    }
  }, [mfaSession, mfaChallenge, loginEmail, user]);

  // Logout
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setMfaSession(null);
    setMfaRequired(false);
    setMfaChallenge(null);
    setLoginEmail(null);

    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminLoginTime');
    localStorage.removeItem('cognitoTokens');
  }, []);

  // Check permission
  const hasPermission = useCallback(async (permission) => {
    if (!token) return false;

    // Quick check from user object if available
    if (user?.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }

    // Fallback: check with API
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_PERMISSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          action: 'check',
          permission
        })
      });

      const data = await response.json();
      return data.hasPermission === true;
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  }, [token, user]);

  // Get user permissions
  const getUserPermissions = useCallback(async () => {
    if (!token) return [];

    // Return from user object if available
    if (user?.permissions && Array.isArray(user.permissions)) {
      return user.permissions;
    }

    // Fallback: fetch from API
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_PERMISSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          action: 'my-permissions'
        })
      });

      const data = await response.json();
      if (data.success && data.user?.permissions) {
        return data.user.permissions;
      }
      return [];
    } catch (error) {
      console.error('Get permissions error:', error);
      return [];
    }
  }, [token, user]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    mfaRequired,
    mfaChallenge,
    login,
    verifyMFA,
    logout,
    hasPermission,
    getUserPermissions
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

